import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Home, Check, ChevronRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { STATUS_COLORS, withAlpha, BRAND_COLORS } from "../../lib/bookingStatus";

/**
 * Kompaktes Ankunfts-Readiness-Widget.
 *
 * Zeigt sich AUSSCHLIESSLICH innerhalb der letzten 24 Stunden vor der
 * Rückkehr (`to_date` ist heute oder morgen) und spiegelt exakt die
 * Ankunfts-Badge-Farbe aus dem Team-Kalender:
 *
 *   • GRÜN + Check-Icon  → "Alles bereit für Sie"
 *       (booking.status === "completed" ODER alle Checklisten-Items ✓)
 *   • ROT  + Haus-Icon   → "Team ist noch dran"
 *       (mind. 1 offener Punkt in arrival/departure Checklist)
 *
 * Datenquelle: dieselbe `bookings`-Prop, die auch der Kalender + Team
 * Dashboard nutzt — kein separater API-Call, damit die Farbe strikt an
 * die Team-Badges gekoppelt bleibt.
 */
export function ArrivalReadinessBadge({ bookings, propertiesById }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const target = useMemo(() => {
    if (!bookings || bookings.length === 0) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const candidates = bookings
      .filter((b) => b.to_date && b.status !== "cancelled" && b.status !== "completed")
      .map((b) => {
        const d = new Date(b.to_date);
        d.setHours(0, 0, 0, 0);
        const hoursDelta = (d.getTime() - now.getTime()) / (60 * 60 * 1000);
        return { ...b, hoursDelta };
      })
      // Fenster: 0-24 h vor Ankunft, also to_date == heute oder morgen.
      .filter((b) => b.hoursDelta >= 0 && b.hoursDelta <= 24)
      .sort((a, b) => a.hoursDelta - b.hoursDelta);
    return candidates[0] || null;
  }, [bookings]);

  if (!target) return null;

  // Ready-Signal folgt STRIKT der Kalender-Statusfarbe:
  //   booking.status === "completed" ⇔ alle Team-Services + Team-Checklisten
  //   sind abgehakt (siehe backend `_all_booking_items_done`). Die private
  //   Abreise-/Anreise-Checkliste des Kunden fließt bewusst NICHT in dieses
  //   Signal — die private Checkliste ist reine Selbst-Erinnerung und darf
  //   den Team-Status nicht beeinflussen.
  const allReady = target.status === "completed";

  const property = propertiesById?.[target.property_id];
  const dayLabel = target.hoursDelta < 1 ? (de ? "Heute" : "Today") : (de ? "Morgen" : "Tomorrow");

  return (
    <Link
      to="/app/arrival"
      data-testid="arrival-readiness-badge"
      data-state={allReady ? "ready" : "in-progress"}
      className="mt-4 flex items-center gap-3 border-2 rounded-lg px-4 py-3 transition-all hover:brightness-105 active:scale-[0.99]"
      style={
        allReady
          ? {
              borderColor: STATUS_COLORS.green,
              background: `linear-gradient(90deg, ${withAlpha(STATUS_COLORS.green, 0.10)} 0%, ${withAlpha(STATUS_COLORS.green, 0.04)} 100%)`,
              boxShadow: `0 0 0 4px ${withAlpha(STATUS_COLORS.green, 0.08)}`,
            }
          : {
              borderColor: STATUS_COLORS.red,
              background: `linear-gradient(90deg, ${withAlpha(STATUS_COLORS.red, 0.10)} 0%, ${withAlpha(STATUS_COLORS.red, 0.04)} 100%)`,
              boxShadow: `0 0 0 4px ${withAlpha(STATUS_COLORS.red, 0.10)}`,
            }
      }
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
        style={{ background: allReady ? STATUS_COLORS.green : STATUS_COLORS.red }}
      >
        {allReady
          ? <Check className="w-5 h-5 text-ivory" strokeWidth={3} />
          : <Home className="w-5 h-5 text-ivory" strokeWidth={2.4} />}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="label-caps mb-0.5"
          style={{ color: allReady ? STATUS_COLORS.green : STATUS_COLORS.red }}
          data-testid="arrival-readiness-label"
        >
          {dayLabel} · {de ? "Ankunft" : "Return"}{property?.name ? (de ? ` bei ${property.name}` : ` at ${property.name}`) : ""}
        </div>
        <div
          className="font-display text-[19px] md:text-[21px] leading-tight"
          style={{ color: allReady ? BRAND_COLORS.greenDark : "#5C1616" }}
          data-testid="arrival-readiness-headline"
        >
          {allReady ? (de ? "Alles bereit für Sie" : "Everything ready for you") : (de ? "Team ist noch dran" : "Team is still on it")}
        </div>
        {!allReady && (
          <div className="text-[11.5px] mt-0.5 opacity-80" style={{ color: "#5C1616" }}>
            {de ? "Team ist noch dabei · vollständige Übersicht öffnen" : "Team is still working · open full overview"}
          </div>
        )}
      </div>
      <ChevronRight
        className="w-4 h-4 shrink-0 opacity-70"
        style={{ color: allReady ? STATUS_COLORS.green : STATUS_COLORS.red }}
        strokeWidth={2}
      />
    </Link>
  );
}
